import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";
import { rewardStatusLabel, rewardTypeLabel } from "../../lib/adminDisplayLabels";

interface InviteRelationRow {
  id: number;
  invitee_account: string;
  phase: string;
  status: string;
  created_at: string;
  confirmed_at?: string | null;
  reward_id?: number | null;
  reward_amount?: number | null;
  dispatch_status?: string | null;
  source_deposit_id?: number | null;
}

interface MemberInviteDetail {
  member: { member_account: string };
  invite_status?: { is_unlocked: boolean; unlocked_at?: string | null };
  invite_code?: { invite_code?: string | null; invite_link?: string | null } | null;
  invite_relations?: InviteRelationRow[];
}

interface InviteCheckResult {
  relation?: InviteRelationRow | null;
  reward?: { id?: number; reward_status?: string; amount?: number } | null;
  deposit?: { id?: number; amount?: number } | null;
  created: boolean;
}

interface ManualReviewRow {
  id: number;
  inviter_account: string;
  invitee_account: string;
  line_nickname?: string | null;
  line_added: number;
  group_joined: number;
  own_channel: number;
  first_deposit_1000_confirmed: number;
  first_deposit_amount?: number | null;
  inviter_invite_unlocked_at?: string | null;
  second_stage_confirmed: number;
  second_deposit_amount?: number | null;
  third_deposit_confirmed: number;
  third_deposit_amount?: number | null;
  review_note?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  join_reward_id?: number | null;
  first_deposit_reward_id?: number | null;
  second_stage_reward_id?: number | null;
  third_deposit_reward_id?: number | null;
  join_reward_status?: string | null;
  first_deposit_reward_status?: string | null;
  second_stage_reward_status?: string | null;
  third_deposit_reward_status?: string | null;
  created_at: string;
  updated_at: string;
}

interface ManualRewardResult {
  created: boolean;
  reward?: { id?: number; reward_type?: string; reward_status?: string; amount?: number } | null;
  review?: ManualReviewRow | null;
}

const initialManualForm = {
  inviterAccount: "",
  inviteeAccount: "",
  lineNickname: "",
  lineAdded: false,
  groupJoined: false,
  ownChannel: false,
  firstDeposit1000Confirmed: false,
  firstDepositAmount: "",
  secondDepositAmount: "",
  thirdDepositAmount: "",
  reviewNote: "",
  reviewedBy: "admin"
};

type ManualForm = typeof initialManualForm;
type ManualRewardType = "invite_join_group_6666" | "invite_first_deposit_50000" | "invite_second_stage_30000" | "invite_third_deposit_10000";

export function AdminInvitePage() {
  const isLocalDev = isLocalDevFrontend();
  const [inviterAccount, setInviterAccount] = useState(isLocalDev ? "test003" : "");
  const [inviteeAccount, setInviteeAccount] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [dispatchStatus, setDispatchStatus] = useState("");
  const [detail, setDetail] = useState<MemberInviteDetail | null>(null);
  const [manualRows, setManualRows] = useState<ManualReviewRow[]>([]);
  const [manualSearch, setManualSearch] = useState("");
  const [manualForm, setManualForm] = useState<ManualForm>({ ...initialManualForm });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [savingManual, setSavingManual] = useState(false);
  const [creatingReward, setCreatingReward] = useState<string | null>(null);

  async function search() {
    const trimmed = inviterAccount.trim();
    if (!trimmed) {
      setDetail(null);
      setError("請輸入邀請人會員帳號");
      return;
    }
    setError("");
    setMessage("");
    try {
      setDetail(await api<MemberInviteDetail>(`/api/admin/members/${encodeURIComponent(trimmed)}`, { headers: adminHeaders() }));
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : "讀取邀友資料失敗");
    }
  }

  async function loadManualRows(account = manualSearch.trim()) {
    setError("");
    try {
      const query = account ? `?account=${encodeURIComponent(account)}` : "";
      setManualRows(await api<ManualReviewRow[]>(`/api/admin/invite/manual-reviews${query}`, { headers: adminHeaders() }));
    } catch (err) {
      setManualRows([]);
      setError(err instanceof Error ? err.message : "讀取人工快審資料失敗");
    }
  }

  async function saveManualReview() {
    setMessage("");
    setError("");
    if (!manualForm.inviterAccount.trim()) {
      setError("請輸入邀請人帳號");
      return;
    }
    if (!manualForm.inviteeAccount.trim()) {
      setError("請輸入好友帳號");
      return;
    }
    setSavingManual(true);
    try {
      const saved = await api<ManualReviewRow>("/api/admin/invite/manual-reviews", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          inviter_account: manualForm.inviterAccount.trim(),
          invitee_account: manualForm.inviteeAccount.trim(),
          line_nickname: manualForm.lineNickname.trim(),
          line_added: manualForm.lineAdded,
          group_joined: manualForm.groupJoined,
          own_channel: manualForm.ownChannel,
          first_deposit_1000_confirmed: Number(manualForm.firstDepositAmount) >= 1000,
          first_deposit_amount: manualForm.firstDepositAmount,
          second_deposit_amount: manualForm.secondDepositAmount,
          second_stage_confirmed: Number(manualForm.secondDepositAmount) > 0,
          third_deposit_confirmed: Number(manualForm.thirdDepositAmount) > 0,
          third_deposit_amount: manualForm.thirdDepositAmount,
          review_note: manualForm.reviewNote.trim(),
          reviewed_by: manualForm.reviewedBy.trim() || "admin"
        })
      });
      setMessage(`已儲存人工快審 #${saved?.id ?? "-"}`);
      setManualSearch(manualForm.inviterAccount.trim());
      await loadManualRows(manualForm.inviterAccount.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "儲存人工快審失敗");
    } finally {
      setSavingManual(false);
    }
  }

  async function createManualReward(row: ManualReviewRow, rewardType: ManualRewardType) {
    setMessage("");
    setError("");
    const label = rewardTypeLabel(rewardType);
    const ok = window.confirm(`確認建立「${label}」？\n建立後只會進入派發中心等待確認派發，不會自動完成。`);
    if (!ok) return;

    setCreatingReward(`${row.id}:${rewardType}`);
    try {
      const result = await api<ManualRewardResult>(`/api/admin/invite/manual-reviews/${row.id}/create-reward`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({ reward_type: rewardType })
      });
      setMessage(result.created
        ? `已建立 ${label} reward #${result.reward?.id ?? "-"}`
        : `${label} 已存在，reward #${result.reward?.id ?? "-"}`);
      await loadManualRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "建立邀友獎勵失敗");
    } finally {
      setCreatingReward(null);
    }
  }

  async function checkFirstDeposit(row: InviteRelationRow) {
    setMessage("");
    setError("");
    setCheckingId(row.id);
    try {
      const result = await api<InviteCheckResult>("/api/admin/invite-check-first-deposit", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          inviter_account: detail?.member.member_account ?? inviterAccount.trim(),
          invitee_account: row.invitee_account
        })
      });
      setMessage(result.created
        ? `已建立邀友首存獎勵 reward #${result.reward?.id ?? "-"}，金額 ${money(result.reward?.amount ?? 50000)}。`
        : `邀友首存獎勵已存在，reward #${result.reward?.id ?? "-"}。`);
      await search();
    } catch (err) {
      setError(err instanceof Error ? err.message : "檢查首存獎勵失敗");
    } finally {
      setCheckingId(null);
    }
  }

  useEffect(() => {
    if (inviterAccount.trim()) search().catch(() => undefined);
    loadManualRows().catch(() => undefined);
  }, []);

  const rows = useMemo(() => {
    const source = detail?.invite_relations ?? [];
    return source.filter((row) => {
      const inviteeMatch = !inviteeAccount.trim() || row.invitee_account.toLowerCase().includes(inviteeAccount.trim().toLowerCase());
      const taskMatch = !taskStatus || row.status === taskStatus;
      const dispatchMatch = !dispatchStatus || row.dispatch_status === dispatchStatus;
      return inviteeMatch && taskMatch && dispatchMatch;
    });
  }, [detail, inviteeAccount, taskStatus, dispatchStatus]);

  const inviteCode = detail?.invite_code?.invite_code ?? "";
  const inviteLink = detail?.invite_code?.invite_link ? `${window.location.origin}${detail.invite_code.invite_link}` : "";

  return (
    <AdminLayout title="邀友管理">
      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-card">
        <div className="admin-card-head">
          <span>人工快審</span>
          <h2>Invite V1.1 邀友人工快審</h2>
        </div>
        <p className="admin-muted">
          本區由客服人工確認好友是否加 LINE、入群、本線渠道、充值與二存 / 三存條件。按鈕只建立可派發單，仍需到派發中心確認已派發。
        </p>
        <div className="invite-manual-form">
          <div className="invite-form-panel">
            <h3>邀請人與好友</h3>
            <div className="invite-form-grid">
              <label>
                邀請人帳號
                <input value={manualForm.inviterAccount} onChange={(event) => setManualForm((form) => ({ ...form, inviterAccount: event.target.value }))} placeholder={isLocalDev ? "test016_inviter" : "請輸入邀請人帳號"} />
              </label>
              <label>
                好友帳號
                <input value={manualForm.inviteeAccount} onChange={(event) => setManualForm((form) => ({ ...form, inviteeAccount: event.target.value }))} placeholder={isLocalDev ? "test016_friend" : "請輸入好友帳號"} />
              </label>
              <label>
                好友 LINE 暱稱
                <input value={manualForm.lineNickname} onChange={(event) => setManualForm((form) => ({ ...form, lineNickname: event.target.value }))} placeholder="請輸入 LINE 暱稱" />
              </label>
              <label>
                客服確認人
                <input value={manualForm.reviewedBy} onChange={(event) => setManualForm((form) => ({ ...form, reviewedBy: event.target.value }))} placeholder="admin" />
              </label>
            </div>
            <p className="admin-muted">邀請人資格：{manualFormInviterUnlockText(manualForm.inviterAccount, manualRows)}</p>
          </div>

          <div className="invite-form-panel">
            <h3>好友狀態</h3>
            <div className="invite-toggle-grid">
              <label className={manualForm.lineAdded ? "invite-toggle checked" : "invite-toggle"}>
                <input type="checkbox" checked={manualForm.lineAdded} onChange={(event) => setManualForm((form) => ({ ...form, lineAdded: event.target.checked }))} />
                <span>已加 LINE</span>
              </label>
              <label className={manualForm.groupJoined ? "invite-toggle checked" : "invite-toggle"}>
                <input type="checkbox" checked={manualForm.groupJoined} onChange={(event) => setManualForm((form) => ({ ...form, groupJoined: event.target.checked }))} />
                <span>已入群</span>
              </label>
              <label className={manualForm.ownChannel ? "invite-toggle checked" : "invite-toggle"}>
                <input type="checkbox" checked={manualForm.ownChannel} onChange={(event) => setManualForm((form) => ({ ...form, ownChannel: event.target.checked }))} />
                <span>本線渠道</span>
              </label>
            </div>
          </div>

          <div className="invite-form-panel">
            <h3>好友充值紀錄</h3>
            <div className="invite-form-grid compact">
              <label>
                好友首存金額
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={manualForm.firstDepositAmount}
                  onChange={(event) => setManualForm((form) => ({ ...form, firstDepositAmount: event.target.value, firstDeposit1000Confirmed: Number(event.target.value) >= 1000 }))}
                  placeholder="首存金額，需 >= 1,000"
                />
              </label>
              <label>
                好友二存金額
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={manualForm.secondDepositAmount}
                  onChange={(event) => setManualForm((form) => ({ ...form, secondDepositAmount: event.target.value }))}
                  placeholder="二存金額"
                />
              </label>
              <label>
                好友三存金額
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={manualForm.thirdDepositAmount}
                  onChange={(event) => setManualForm((form) => ({ ...form, thirdDepositAmount: event.target.value }))}
                  placeholder="三存金額"
                />
              </label>
            </div>
          </div>

          <div className="invite-form-panel invite-rule-panel">
            <h3>獎勵規則提示</h3>
            <div className="summary-list">
              <p>入群獎 6,666：好友入群後，邀請人可領；不要求邀請人先首充。</p>
              <p>首存獎 50,000：好友首存大於等於 1,000，且邀請人已解鎖邀友資格。</p>
              <p>二存獎 30,000：好友二存金額有填，且邀請人已解鎖邀友資格。</p>
              <p>三存獎 10,000：好友三存金額有填，且邀請人已解鎖邀友資格。</p>
            </div>
          </div>

          <div className="invite-form-panel">
            <h3>客服備註與儲存</h3>
            <label className="admin-block-label">
              客服人工確認備註
              <textarea value={manualForm.reviewNote} onChange={(event) => setManualForm((form) => ({ ...form, reviewNote: event.target.value }))} placeholder="請記錄客服確認內容、LINE 對話重點或補充說明" />
            </label>
            <div className="table-actions">
              <button onClick={saveManualReview} disabled={savingManual}>{savingManual ? "儲存中..." : "儲存人工快審"}</button>
              <button className="secondary" onClick={() => setManualForm({ ...initialManualForm })}>清空表單</button>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>人工快審清單</span>
          <h2>邀友獎勵建單</h2>
        </div>
        <AdminFilterBar>
          <label>
            查詢帳號
            <input value={manualSearch} onChange={(event) => setManualSearch(event.target.value)} placeholder="邀請人、好友、LINE、快審 ID 或 reward ID" />
          </label>
          <button onClick={() => loadManualRows()}>查詢人工快審</button>
          <button className="secondary" onClick={() => { setManualSearch(""); loadManualRows(""); }}>全部</button>
        </AdminFilterBar>
        <div className="invite-review-list">
          {manualRows.map((row) => {
            const rewardTypes: ManualRewardType[] = [
              "invite_join_group_6666",
              "invite_first_deposit_50000",
              "invite_second_stage_30000",
              "invite_third_deposit_10000"
            ];
            return (
              <article className="invite-review-card" key={row.id}>
                <div className="invite-review-main">
                  <div className="invite-review-identity">
                    <span className="admin-muted">快審 #{row.id}｜建立 {shortDate(row.created_at)}</span>
                    <h3>{row.invitee_account}</h3>
                    <p>邀請人：{row.inviter_account}</p>
                    <p>LINE：{row.line_nickname || "-"}</p>
                  </div>
                  <div className="invite-badge-row">
                    <span className={row.line_added ? "status-pill good" : "status-pill muted"}>LINE：{row.line_added ? "已加" : "未確認"}</span>
                    <span className={row.group_joined ? "status-pill good" : "status-pill muted"}>入群：{row.group_joined ? "已入群" : "未入群"}</span>
                    <span className={row.own_channel ? "status-pill good" : "status-pill danger"}>渠道：{row.own_channel ? "本線" : "非本線"}</span>
                    <span className={isInviterUnlocked(row) ? "status-pill good" : "status-pill warn"}>邀請人資格：{isInviterUnlocked(row) ? "已解鎖" : "尚未解鎖"}</span>
                  </div>
                  <div className="invite-review-grid">
                    <div>
                      <span className="invite-card-label">充值摘要</span>
                      <p>首存：{amountOrDash(row.first_deposit_amount)}</p>
                      <p>二存：{depositAmountText(row.second_deposit_amount, row.second_stage_reward_id)}</p>
                      <p>三存：{depositAmountText(row.third_deposit_amount, row.third_deposit_reward_id)}</p>
                    </div>
                    <div className="invite-reward-summary">
                      <span className="invite-card-label">獎勵摘要</span>
                      {rewardTypes.map((type) => (
                        <span className={manualRewardBadgeClass(row, type)} key={type}>
                          {shortManualRewardLabel(type)}：{manualRewardShortStatus(row, type)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <details className="invite-review-detail">
                  <summary>查看明細與操作</summary>
                  <div className="invite-detail-grid">
                    <section>
                      <h4>好友完整狀態</h4>
                      <p>已加 LINE：{row.line_added ? "是" : "未確認"}</p>
                      <p>已入群：{row.group_joined ? "是" : "未確認"}</p>
                      <p>本線渠道：{row.own_channel ? "是" : "否"}</p>
                      <p>邀請人資格：{isInviterUnlocked(row) ? "已解鎖" : "尚未解鎖"}</p>
                      <p>客服確認人：{row.reviewed_by || "-"}</p>
                      <p>客服備註：{row.review_note || "-"}</p>
                    </section>
                    <section>
                      <h4>充值明細</h4>
                      <p>首存金額：{amountOrDash(row.first_deposit_amount)}</p>
                      <p>二存金額：{depositAmountText(row.second_deposit_amount, row.second_stage_reward_id)}</p>
                      <p>三存金額：{depositAmountText(row.third_deposit_amount, row.third_deposit_reward_id)}</p>
                    </section>
                  </div>

                  <div className="invite-reward-detail-list">
                    {rewardTypes.map((type) => {
                      const existingRewardId = manualRewardId(row, type);
                      const reason = existingRewardId ? "" : manualRewardBlockReason(row, type);
                      const disabled = Boolean(existingRewardId) || Boolean(reason) || creatingReward === `${row.id}:${type}`;
                      return (
                        <div className="invite-reward-detail" key={type}>
                          <div>
                            <strong>{rewardTypeLabel(type)}</strong>
                            <p>派發 ID：{existingRewardId ? `#${existingRewardId}` : "未建立"}</p>
                            <p>狀態：{manualRewardStatusText(row, type)}</p>
                            <p>{reason ? `不可建立，${reason}` : existingRewardId ? "不可重複操作" : "條件符合，可建立"}</p>
                          </div>
                          {existingRewardId ? (
                            <span className={manualRewardBadgeClass(row, type)}>{manualRewardShortStatus(row, type)}</span>
                          ) : reason ? (
                            <span className="status-pill danger">不可建立</span>
                          ) : (
                            <button
                              className="secondary mini-button"
                              disabled={disabled}
                              onClick={() => createManualReward(row, type)}
                            >
                              {manualRewardButtonLabel(type)}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </details>
              </article>
            );
          })}
          {!manualRows.length ? (
            <p className="empty-state">目前沒有符合條件的人工快審資料。</p>
          ) : null}
        </div>
      </section>

      <details className="admin-card invite-backup-flow">
        <summary>備用邀請碼流程</summary>
        <div className="admin-card-head">
          <span>備用邀請碼流程</span>
          <h2>Invite V1 備用流程</h2>
        </div>
        <AdminFilterBar>
          <label>
            邀請人帳號
            <input value={inviterAccount} onChange={(event) => setInviterAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "請輸入會員帳號"} />
          </label>
          <label>
            好友帳號
            <input value={inviteeAccount} onChange={(event) => setInviteeAccount(event.target.value)} placeholder={isLocalDev ? "test004" : "請輸入好友帳號"} />
          </label>
          <label>
            任務狀態
            <select value={taskStatus} onChange={(event) => setTaskStatus(event.target.value)}>
              <option value="">全部</option>
              <option value="active">已綁定</option>
              <option value="completed">已完成</option>
            </select>
          </label>
          <label>
            派發狀態
            <select value={dispatchStatus} onChange={(event) => setDispatchStatus(event.target.value)}>
              <option value="">全部</option>
              <option value="ready">可派發 / 等待派發</option>
              <option value="completed">已完成</option>
              <option value="failed">失敗</option>
            </select>
          </label>
          <button onClick={search}>查詢</button>
          <button
            className="secondary"
            onClick={() => {
              setInviterAccount(isLocalDev ? "test003" : "");
              setInviteeAccount("");
              setTaskStatus("");
              setDispatchStatus("");
              setError("");
              setMessage("");
              setDetail(null);
            }}
          >
            重設
          </button>
        </AdminFilterBar>
        <p className="admin-muted">
          備用邀請碼流程只支援「好友首存滿 1,000，邀請人取得 50,000」。目前前台主流程已改為 LINE 導流與人工快審。
        </p>
        {detail ? (
          <div className="summary-list">
            <p>邀友資格：{detail.invite_status?.is_unlocked ? `已開啟，${shortDate(detail.invite_status.unlocked_at)}` : "尚未開啟"}</p>
            <p>邀請碼：{inviteCode || "尚未建立"}</p>
            <p>邀請連結：{inviteLink || "-"}</p>
          </div>
        ) : null}

        <div className="admin-card-head">
          <span>備用邀請碼關係</span>
          <h2>好友首存檢查</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>邀請人</th>
                <th>好友帳號</th>
                <th>階段</th>
                <th>關係狀態</th>
                <th>好友首存 ID</th>
                <th>派發 ID</th>
                <th>派發金額</th>
                <th>派發狀態</th>
                <th>建立時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{detail?.member.member_account ?? inviterAccount}</td>
                  <td>{row.invitee_account}</td>
                  <td>{invitePhaseLabel(row.phase)}</td>
                  <td>{inviteStatusLabel(row.status)}</td>
                  <td>{row.source_deposit_id ?? "-"}</td>
                  <td>{row.reward_id ?? "-"}</td>
                  <td>{money(row.reward_amount ?? 50000)}</td>
                  <td>{rewardStatusLabel(row.dispatch_status ?? "")}</td>
                  <td>{shortDate(row.created_at)}</td>
                  <td>
                    {row.reward_id ? (
                      <span className="admin-muted">已建立派發</span>
                    ) : (
                      <button className="secondary mini-button" disabled={checkingId === row.id} onClick={() => checkFirstDeposit(row)}>
                        {checkingId === row.id ? "檢查中..." : "檢查首存並建立派發"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr><td colSpan={10}>目前沒有符合條件的備用邀請碼資料。</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </details>
    </AdminLayout>
  );
}

function invitePhaseLabel(phase: string) {
  if (phase === "invite_first_deposit") return "好友首存單筆滿 1,000";
  if (phase === "invite_manual") return "人工快審";
  return phase;
}

function inviteStatusLabel(status: string) {
  if (status === "active") return "已建立";
  if (status === "completed") return "已完成";
  if (status === "pending") return "待確認";
  return status;
}

function yesNo(label: string, value: number) {
  return `${label}：${value ? "是" : "否"}`;
}

function amountOrDash(value?: number | null) {
  return Number(value ?? 0) > 0 ? money(Number(value)) : "未填";
}

function depositAmountText(value?: number | null, rewardId?: number | null) {
  if (Number(value ?? 0) > 0) return money(Number(value));
  return rewardId ? "舊資料未記錄" : "未填";
}

function rewardRef(id?: number | null, status?: string | null) {
  if (!id) return "尚未建立";
  if (status === "completed") return `#${id} 已派發`;
  if (status === "ready") return `#${id} 待派發`;
  return `#${id}｜${rewardStatusLabel(status ?? "")}`;
}

function inviterUnlockText(row: ManualReviewRow) {
  return row.inviter_invite_unlocked_at ? `已解鎖，${shortDate(row.inviter_invite_unlocked_at)}` : "尚未解鎖";
}

function manualFormInviterUnlockText(inviterAccount: string, rows: ManualReviewRow[]) {
  const account = inviterAccount.trim();
  if (!account) return "請先輸入邀請人帳號";
  const matched = rows.find((row) => row.inviter_account === account);
  if (!matched) return "尚未查詢 / 儲存後由系統判定";
  return matched.inviter_invite_unlocked_at ? `已解鎖，${shortDate(matched.inviter_invite_unlocked_at)}` : "尚未解鎖";
}

function isInviterUnlocked(row: ManualReviewRow) {
  return Boolean(row.inviter_invite_unlocked_at);
}

function secondDepositStateText(row: ManualReviewRow) {
  if (row.second_stage_reward_id) return "已建立獎勵";
  if (Number(row.second_deposit_amount ?? 0) <= 0) return "金額未填，暫不可建獎";
  return "金額已填，可依資格建獎";
}

function thirdDepositStateText(row: ManualReviewRow) {
  if (row.third_deposit_reward_id) return "已建立獎勵";
  if (Number(row.third_deposit_amount ?? 0) <= 0) return "金額未填，暫不可建獎";
  return "金額已填，可依資格建獎";
}

function manualRewardId(row: ManualReviewRow, rewardType: ManualRewardType) {
  if (rewardType === "invite_join_group_6666") return row.join_reward_id;
  if (rewardType === "invite_first_deposit_50000") return row.first_deposit_reward_id;
  if (rewardType === "invite_second_stage_30000") return row.second_stage_reward_id;
  return row.third_deposit_reward_id;
}

function manualRewardStatus(row: ManualReviewRow, rewardType: ManualRewardType) {
  if (rewardType === "invite_join_group_6666") return row.join_reward_status;
  if (rewardType === "invite_first_deposit_50000") return row.first_deposit_reward_status;
  if (rewardType === "invite_second_stage_30000") return row.second_stage_reward_status;
  return row.third_deposit_reward_status;
}

function manualRewardBlockReason(row: ManualReviewRow, rewardType: ManualRewardType) {
  if (rewardType === "invite_join_group_6666") {
    if (!row.line_added) return "好友尚未加 LINE";
    if (!row.group_joined) return "好友尚未入群";
    return "";
  }
  if (!row.own_channel) return "非本線渠道";
  if (!isInviterUnlocked(row)) return "邀請人尚未解鎖";
  if (rewardType === "invite_first_deposit_50000") {
    if (Number(row.first_deposit_amount ?? 0) < 1000) return "首存金額未達 1,000";
    return "";
  }
  if (rewardType === "invite_second_stage_30000") {
    if (Number(row.second_deposit_amount ?? 0) <= 0) return "二存金額未填";
    return "";
  }
  if (Number(row.second_deposit_amount ?? 0) <= 0) return "二存金額未填，不能跳過二存";
  if (Number(row.third_deposit_amount ?? 0) <= 0) return "三存金額未填";
  return "";
}

function manualRewardStatusText(row: ManualReviewRow, rewardType: ManualRewardType) {
  const id = manualRewardId(row, rewardType);
  if (id) return rewardRef(id, manualRewardStatus(row, rewardType));
  const reason = manualRewardBlockReason(row, rewardType);
  return reason ? `不可建立，${reason}` : "可建立";
}

function manualRewardShortStatus(row: ManualReviewRow, rewardType: ManualRewardType) {
  const id = manualRewardId(row, rewardType);
  if (id) {
    const status = manualRewardStatus(row, rewardType);
    if (status === "completed") return "已派發";
    if (status === "ready") return "待派發";
    return rewardStatusLabel(status ?? "");
  }
  return manualRewardBlockReason(row, rewardType) ? "不可建立" : "可建立";
}

function manualRewardBadgeClass(row: ManualReviewRow, rewardType: ManualRewardType) {
  const id = manualRewardId(row, rewardType);
  if (id) {
    const status = manualRewardStatus(row, rewardType);
    if (status === "completed") return "status-pill good";
    if (status === "ready") return "status-pill warn";
    return "status-pill muted";
  }
  return manualRewardBlockReason(row, rewardType) ? "status-pill danger" : "status-pill good";
}

function shortManualRewardLabel(rewardType: ManualRewardType) {
  if (rewardType === "invite_join_group_6666") return "入群獎 6,666";
  if (rewardType === "invite_first_deposit_50000") return "首存獎 50,000";
  if (rewardType === "invite_second_stage_30000") return "二存獎 30,000";
  return "三存獎 10,000";
}

function manualRewardButtonLabel(rewardType: ManualRewardType) {
  if (rewardType === "invite_join_group_6666") return "建立邀請人入群獎 6,666";
  if (rewardType === "invite_first_deposit_50000") return "建立邀請人首存獎 50,000";
  if (rewardType === "invite_second_stage_30000") return "建立邀請人二存獎 30,000";
  return "建立邀請人三存獎 10,000";
}
